import { Button, Typography, useBreakpoint } from "@povio/ui";
import { cx } from "class-variance-authority";
import clsx from "clsx";
import Image from "next/image";
import { useState } from "react";

import { ChevronDownIcon } from "@/assets/icons/general/ChevronDown";
import { InfoIcon } from "@/assets/icons/general/Info";
import { PaperIcon } from "@/assets/icons/ui/PaperIcon";
import LossdogScoreFitImage from "@/assets/images/home/jobfit.png";
import LossdogScoreSectionImage from "@/assets/images/home/jobfit-bg@2x.png";
import LossdogScoreFitRoundedImage from "@/assets/images/home/jobfitrounded.png";
import { DemoClickWrapper } from "@/components/shared/demo/DemoClickWrapper";
import { ModalSheet } from "@/components/shared/modal/ModalSheet";
import CustomTooltipTrigger from "@/components/ui/overlays/Tooltip/CustomTooltipTrigger";
import { Tooltip } from "@/components/ui/overlays/Tooltip/Tooltip";
import DotsLoader from "@/components/ui/status/Loader/ThreeDotsLoader";
import { TourSelectors } from "@/config/tour.config";
import { CommonModels } from "@/data/common/common.models";
import type { WatchlistModels } from "@/data/watchlist/watchlist.models";

interface IProps {
  jobFitData?: WatchlistModels.WatchlistJobFitDto | null;
  preloadedJobFit?: { fitScore: number; status: string } | null;
  isLoadingJobFit: boolean;
  calculateJobFit: () => void;
}

const JobFitSection = ({ jobFitData, preloadedJobFit, isLoadingJobFit, calculateJobFit }: IProps) => {
  const [selectedActionIndex, setSelectedActionIndex] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleItemClick = (index: number) => {
    setSelectedActionIndex(index);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedActionIndex(null);
  };

  const selectedAction =
    selectedActionIndex !== null && jobFitData?.actions ? jobFitData.actions[selectedActionIndex] : null;

  if (jobFitData) {
    if (jobFitData.status === CommonModels.StatusEnum.completed) {
      return (
        <>
          <CompletedJobFitComponent
            fitScore={jobFitData.fitScore ?? 0}
            actions={jobFitData.actions}
            isLoadingJobFit={false}
            calculateJobFit={calculateJobFit}
            handleItemClick={handleItemClick}
          />
          <ModalSheet
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            title={selectedAction?.title || ""}
            modalClassName="z-50"
            alwaysModal
          >
            <Typography
              size="body-3"
              className="text-text-default-2"
            >
              {selectedAction?.description || ""}
            </Typography>
          </ModalSheet>
        </>
      );
    } else if (jobFitData.status === CommonModels.StatusEnum.failed) {
      return <FailedJobFitComponent calculateJobFit={calculateJobFit} />;
    } else if (
      jobFitData.status === CommonModels.StatusEnum.processing ||
      jobFitData.status === CommonModels.StatusEnum.enqueued
    ) {
      return <CalculatingJobFitComponent />;
    }
  }

  // Preloaded score only: show score immediately, suggested actions loading
  if (preloadedJobFit?.status === CommonModels.StatusEnum.completed) {
    return (
      <CompletedJobFitComponent
        fitScore={preloadedJobFit.fitScore}
        actions={jobFitData?.actions}
        isLoadingJobFit={isLoadingJobFit}
        calculateJobFit={calculateJobFit}
        handleItemClick={handleItemClick}
      />
    );
  }

  // Default, no data, calc now cta
  return (
    <DefaultJobFitComponent
      calculateJobFit={calculateJobFit}
      isLoadingJobFit={isLoadingJobFit}
    />
  );
};

interface DefaultJobFitComponentProps {
  calculateJobFit: () => void;
  isLoadingJobFit: boolean;
}

const DefaultJobFitComponent = ({ calculateJobFit, isLoadingJobFit }: DefaultJobFitComponentProps) => {
  return (
    <div
      id={TourSelectors.JobFitSection}
      className="flex w-full flex-col items-center justify-center"
    >
      <Image
        src={LossdogScoreFitImage}
        alt="Job Fit Section"
        className="w-full"
      />
      <div className="flex w-full flex-col items-center justify-center gap-6 bg-interactive-contained-primary-idle p-4">
        <Typography
          size="title-3"
          variant="prominent-1"
          className="text-center text-text-inverted-1"
        >
          Let&apos;s See How Your Data Matches Up!
        </Typography>
        <Button
          variant="contained"
          color="secondary"
          icon={PaperIcon}
          onPress={calculateJobFit}
          width="hug"
          size="s"
          className="w-full max-w-80"
          isLoading={isLoadingJobFit}
        >
          Calculate Your Job Fit
        </Button>
      </div>
    </div>
  );
};

interface CompletedJobFitComponentProps {
  fitScore: number;
  actions?: WatchlistModels.WatchlistJobFitActionDto[];
  isLoadingJobFit: boolean;
  calculateJobFit: () => void;
  handleItemClick: (index: number) => void;
}

const CompletedJobFitComponent = ({
  fitScore,
  actions = [],
  isLoadingJobFit,
  calculateJobFit,
  handleItemClick,
}: CompletedJobFitComponentProps) => {
  const isDesktop = useBreakpoint("xl");
  const sortedActions = [...(actions ?? [])].sort((a, b) => a.step - b.step).slice(0, 3);
  const hasActions = sortedActions.length > 0;

  return (
    <div
      id={TourSelectors.JobFitSection}
      className="flex w-full flex-col overflow-hidden border border-elevation-outline-default-1"
    >
      <div className={clsx("flex flex-col gap-4 p-4", !isDesktop && "items-center")}>
        <div className="flex flex-row items-center justify-start gap-1-5">
          <Typography
            size="title-5"
            variant="prominent-1"
            className="text-text-default-1"
          >
            Does This Job Fit You?
          </Typography>

          <Tooltip
            text="This score measures your personal qualifications for this position."
            placement="top"
          >
            <CustomTooltipTrigger>
              <InfoIcon className="inline-block h-6 w-6" />
            </CustomTooltipTrigger>
          </Tooltip>
        </div>

        <div className="flex w-full flex-col gap-6 py-4">
          <div className="flex flex-row items-center justify-start">
            <Image
              src={LossdogScoreFitRoundedImage}
              alt="Job Fit Section"
              height={100}
            />
            <div className="flex h-[100px] w-full flex-col items-center justify-center rounded-r-xs bg-elevation-fill-default-2">
              <Typography
                size="title-1"
                variant="prominent-1"
                className="font-boyrun! text-text-default-1 leading-6!"
              >
                Job Fit
              </Typography>
              <Typography
                size="headline-1"
                variant="prominent-1"
                className="text-interactive-contained-primary-idle"
              >
                {fitScore}%
              </Typography>
            </div>
          </div>
          <DemoClickWrapper>
            <Button
              variant="contained"
              color="secondary"
              onPress={calculateJobFit}
              isLoading={isLoadingJobFit}
              icon={PaperIcon}
              size="s"
            >
              Recalculate Job Fit
            </Button>
          </DemoClickWrapper>
        </div>
      </div>
      <div className="flex w-full flex-1 flex-col gap-2 overflow-visible rounded-sm p-4">
        <Typography
          size="title-5"
          variant="prominent-1"
          className="text-text-default-1"
        >
          Suggested Actions
        </Typography>
        <div className="relative flex w-full flex-1 flex-col overflow-visible bg-elevation-fill-default-0">
          {hasActions ? (
            sortedActions.map((action, index) => (
              <SuggestedActionItem
                key={action.step}
                index={index}
                title={action?.title || ""}
                description={action?.description || ""}
                onToggle={() => handleItemClick(index)}
              />
            ))
          ) : (
            <div className="flex w-full items-center justify-center py-6">
              <DotsLoader
                size={5}
                className="ml-1"
                dotClassName="bg-text-default-3!"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface FailedJobFitComponentProps {
  calculateJobFit: () => void;
}

const FailedJobFitComponent = ({ calculateJobFit }: FailedJobFitComponentProps) => (
  <div
    style={{ backgroundImage: `url(${LossdogScoreSectionImage.src})` }}
    className="flex h-auto max-h-[400px] min-h-[248px] w-full flex-row items-center rounded-lg bg-center bg-cover bg-no-repeat pt-2-5 pr-4 pb-2 pl-4"
  >
    <div className="flex h-full w-full flex-col items-center justify-center gap-2">
      <Typography
        size="title-5"
        variant="prominent-1"
        className="text-black"
      >
        Failed to calculate job fit
      </Typography>

      <div>
        <Button onPress={calculateJobFit}>Calculate Job Fit</Button>
      </div>
    </div>
  </div>
);

const CalculatingJobFitComponent = () => (
  <div
    id={TourSelectors.JobFitSection}
    style={{ backgroundImage: `url(${LossdogScoreSectionImage.src})` }}
    className="flex h-auto max-h-[400px] min-h-[248px] w-full flex-row items-center rounded-lg bg-center bg-cover bg-no-repeat pt-2-5 pr-4 pb-2 pl-4"
  >
    <div className="flex h-full w-full items-center justify-center">
      <Typography
        size="title-5"
        variant="prominent-1"
        className="text-text-default-3"
      >
        Calculating job fit percentage
      </Typography>
      <DotsLoader
        size={5}
        className="mt-2 ml-1"
        dotClassName="bg-text-default-3!"
      />
    </div>
  </div>
);

interface SuggestedActionItemProps {
  title: string;
  description: string;
  onToggle: () => void;
  index: number;
}

const SuggestedActionItem = ({ title, onToggle, index }: SuggestedActionItemProps) => {
  return (
    <div
      className={cx(
        "flex w-full cursor-pointer flex-row items-center justify-between gap-3 border-secondary-200 border-t border-t-transparent border-r border-b px-2 py-2-5 transition-colors hover:bg-secondary-300",
      )}
      onClick={onToggle}
    >
      <Typography
        size="label-2"
        className="text-text-default-1"
      >
        <span className="px-2">{index + 1}</span>
        <Typography
          size="label-2"
          className="ml-3 text-text-default-1"
          as="span"
        >
          {title}
        </Typography>
      </Typography>

      <ChevronDownIcon className="h-4 w-4 -rotate-90 text-text-default-1" />
    </div>
  );
};

export default JobFitSection;
